import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import styled from '@emotion/styled';
import { Typography } from '@innovamat/glimmer-components';
import { useGetModulesOfCourseQuery } from '@innovamat/glow-api-client';
import { usePrint } from '@innovamat/hooks';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEnvVariables } from '../../../env-variables-manager';
import { APP_PATHS } from '../../../navigation/utils/urls';
import { PrintFooter } from '../print-guides';
import type { Language } from './download-documents/select-options';
import { getPersonalizedPracticeFileName } from './get-file-name';
import {
  ActivityCard,
  ActivityGridRow,
  ActivityImage,
  ActivityInfo,
  Bold,
  FileDescription,
  FileTitle,
  GlobalStyle,
  HeaderBar,
  KeepTogether,
  ModuleName,
  StandardInfo,
  StandardsContainer,
  StandardSection,
  Stretch,
  StyledTable,
  TabletTitle,
  TBodyCell,
  THeadCell,
  UnlockedAppletsTitle,
  Wrapper,
} from './print-us-styled';
import { getTranslation } from './translations';

type Activity = {
  codenamepack: string;
  title: string;
  subcategory?: string | null;
  screenshot: string;
  scene: string;
  pack: number;
};

type Standard = {
  code: string;
  description: string;
  activities: Activity[];
};

type Module = {
  stretch: string;
  title: string;
  standards: Standard[];
};

type PrintModuleProps = Module & {
  hideStandards?: boolean;
  isLastItem?: boolean;
  language: Language;
};

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: 100%;
  gap: 0.25rem;
`;

const SubcategoryContainer = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  height: 1.7rem;
`;

const SubcategoryText = styled.p`
  color: #718482;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 0.85rem;
`;

const groupByRows = (activities: Activity[], n: number): Activity[][] => {
  const rows = [];
  for (let i = 0; i < activities.length; i += n) {
    rows.push(activities.slice(i, i + n));
  }
  return rows;
};

const ActivityCardItem = ({
  activity,
  isFirstLineOfGroup,
  t,
}: {
  activity: Activity;
  isFirstLineOfGroup?: boolean;
  t: TFunction<'translation', undefined, 'translation'>;
}): EmotionJSX.Element => {
  const { codenamepack, screenshot, subcategory, title, scene, pack } =
    activity;
  const { MANAGER_NEXT } = useEnvVariables();

  return (
    <a
      href={`${MANAGER_NEXT}${APP_PATHS.APPLET_BY_SCENE_URL}/${scene}/${pack}`}
      target="_blank"
      rel="noreferrer"
    >
      <ActivityCard
        isFirstLineOfGroup={isFirstLineOfGroup}
        className="activity-card"
      >
        <ActivityImage
          baseUrl={MANAGER_NEXT}
          hasOptimization
          src={screenshot}
          alt={title}
          width={322}
          height={181}
          quality={100}
          priority
          sizes="(max-width: 768px) 100vw, 322px"
        />
        <InfoContainer>
          <ActivityInfo>
            <Bold>{codenamepack}</Bold> - {t(title, { lng: 'en' })}
          </ActivityInfo>
          <SubcategoryContainer>
            <SubcategoryText>{subcategory}</SubcategoryText>
          </SubcategoryContainer>
        </InfoContainer>
      </ActivityCard>
    </a>
  );
};

const PrintModule: React.FC<PrintModuleProps> = ({
  stretch,
  title,
  standards,
  hideStandards,
  isLastItem,
  language,
}) => {
  const { t } = useTranslation();

  return (
    <StyledTable isLastItem={isLastItem}>
      <thead>
        <tr>
          <THeadCell colSpan={1}>
            <HeaderBar className="header-bar">
              <FileTitle>
                {getTranslation(language, 'personalizedPractice.title')}
              </FileTitle>
              <TabletTitle>
                <Stretch>{t(stretch)}</Stretch>
                <ModuleName> - {t(title)}</ModuleName>
              </TabletTitle>
            </HeaderBar>
          </THeadCell>
        </tr>
      </thead>
      <tbody>
        <tr>
          <TBodyCell>
            <FileDescription>
              <Trans
                i18nKey="personalizedPractice.description"
                defaults={getTranslation(
                  language,
                  'personalizedPractice.description'
                )}
                components={{
                  b: <Bold />,
                  p: <p />,
                  a: (
                    <Typography.Link2
                      href={'https://manager.innovamat.com/applets'}
                      target="_blank"
                    />
                  ),
                }}
              />
            </FileDescription>
            <UnlockedAppletsTitle>
              {getTranslation(language, 'personalizedPractice.unlockedApplets')}
            </UnlockedAppletsTitle>
            <StandardsContainer>
              {standards.map(({ code, description, activities }, idx) => {
                if (activities.length === 0) {
                  return null;
                }

                const rows = groupByRows(activities, 2);

                return (
                  <StandardSection key={idx} className="standard-section">
                    <KeepTogether>
                      {!hideStandards && (
                        <StandardInfo>
                          <Bold>{code}</Bold>:{' '}
                          {t(description, {
                            lng: language === 'es' ? 'es_la' : language,
                          })}
                        </StandardInfo>
                      )}
                      {rows.length > 0 && (
                        <ActivityGridRow hideStandards={hideStandards}>
                          {rows[0].map((activity, activityIndex) => (
                            <ActivityCardItem
                              key={activityIndex}
                              activity={activity}
                              t={t}
                            />
                          ))}
                        </ActivityGridRow>
                      )}
                    </KeepTogether>
                    {rows.slice(1).map((row, rowIndex) => (
                      <ActivityGridRow key={rowIndex}>
                        {row.map((activity, activityIndex) => (
                          <ActivityCardItem
                            isFirstLineOfGroup={rowIndex === 0}
                            key={activityIndex}
                            activity={activity}
                            t={t}
                          />
                        ))}
                      </ActivityGridRow>
                    ))}
                  </StandardSection>
                );
              })}
            </StandardsContainer>
          </TBodyCell>
        </tr>
      </tbody>
    </StyledTable>
  );
};

export function PrintPersonalizedPractice(): EmotionJSX.Element {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const stretch = searchParams.get('stretch');
  const module = searchParams.get('module');
  const hideStandards = searchParams.get('hideStandards') === 'true';
  const noWrapper = searchParams.get('noWrapper') === 'true';
  const language = searchParams.get('language');

  const { data } = useGetModulesOfCourseQuery({
    courseUuid: courseId || '',
    stretch,
    module,
    language: language || 'en',
    hideStandards: hideStandards ? 'true' : 'false',
  });

  const { componentRef, onPrint, loading } = usePrint(
    getPersonalizedPracticeFileName({
      courseId: courseId || '',
      stretch,
      module,
      hideStandards,
      language: (language || 'en') as Language,
    })
  );

  const content = (
    <div ref={componentRef}>
      {data?.modulesOfCourse.map((module, moduleIndex) => (
        <PrintModule
          key={moduleIndex}
          {...module}
          hideStandards={hideStandards}
          isLastItem={moduleIndex === data?.modulesOfCourse.length - 1}
          language={(language || 'en') as Language}
        />
      ))}
    </div>
  );

  return (
    <>
      <GlobalStyle />
      {noWrapper ? (
        content
      ) : (
        <Wrapper>
          <PrintFooter onPrint={onPrint} loading={loading} />
          {content}
        </Wrapper>
      )}
    </>
  );
}
